// hooks/useEndowment.js
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as endowmentService from '../services/endowment.service';
import { checkPaymentStatus } from '../services/api.service';
import { logEvent } from '../analytics';

export const useEndowment = () => {
  const { user, token } = useAuth();

  // Core endowment state
  const [endowment, setEndowment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [endowmentExists, setEndowmentExists] = useState(false);

  // Form state
  const [alias, setAlias] = useState('');
  const [recipient, setRecipient] = useState('');
  const [releaseDate, setReleaseDate] = useState('');
  const [maxReleaseDate, setMaxReleaseDate] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [recipientStatus, setRecipientStatus] = useState(null);
  const [originalValues, setOriginalValues] = useState({
    alias: '',
    recipient: '',
    releaseDate: ''
  });

  // --- Analytics helpers (no PII) ---
const emailDomain = (val) =>
  (typeof val === 'string' && val.includes('@') ? val.split('@').pop().toLowerCase() : 'invalid');

const safeLen = (arr) => (Array.isArray(arr) ? arr.length : 0);

  // Selected items state
  const [selectedItems, setSelectedItems] = useState({
    files: [],
    passwords: [],
    banking: [],
    blockchain: []
  });

  // Operation states
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddingToEndowment, setIsAddingToEndowment] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'success'
  });

  // Alert helper
  const showAlert = (message, variant = 'success', duration = 5000) => {
    setAlert({ show: true, message, variant });

    if (duration > 0) {
      setTimeout(() => {
        setAlert(prev => ({ ...prev, show: false }));
      }, duration);
    }
  };

  // Load endowment data
  const loadEndowment = async (forceClearCache = false) => {
    setIsLoading(true);
    try {
      if (forceClearCache) {
        endowmentService.clearCache();
      }

      const endowments = await endowmentService.getInheritanceList(token, forceClearCache);

      if (endowments.length > 0) {
        try {

          let selectedEndowment = null;
          let validEndowments = [];

          // Step 1: Check each endowment file and find ones that belong to this user
          for (const endowmentFile of endowments) {
            try {

              // Parse the file to get recipient email
              const endowmentData = await endowmentService.getInheritance(endowmentFile.name, token);

              // Check if this inheritance exists in database and belongs to current user
              const status = await endowmentService.getInheritanceStatus({
                creatorEmail: user.Email,
                recipientEmail: endowmentData.account
              }, token);

              if (status?.exists) {
                validEndowments.push({
                  file: endowmentFile,
                  data: endowmentData,
                  status: status,
                  lastModified: new Date(endowmentFile.LastModified)
                });
              } else {
              }
            } catch (error) {
              console.warn(`⚠️  Skipping invalid endowment file: ${endowmentFile.name}`, error.message);
              continue;
            }
          }

          if (validEndowments.length === 0) {
            setEndowment(null);
            setEndowmentExists(false);
            resetForm();
            setIsLoading(false);
            return;
          }

          // Step 2: Sort by last modified date (newest first) and take the most recent
          validEndowments.sort((a, b) => b.lastModified - a.lastModified);
          selectedEndowment = validEndowments[0];

          // If there are multiple valid endowments, log a warning
          if (validEndowments.length > 1) {
            console.warn(`⚠️  Found ${validEndowments.length} valid endowments for this user. Using the most recent one.`);
            console.warn('Other files:', validEndowments.slice(1).map(e => `${e.file.name} (${e.lastModified})`));
          }

          const { file: latestEndowment, data: endowmentData, status } = selectedEndowment;

          // Get duration limits
          let durationResult = null;
          try {
            durationResult = await endowmentService.getDuration({
              creatorEmail: user.Email,
              recipientEmail: endowmentData.account
            }, token);

            if (durationResult?.date) {
              setMaxReleaseDate(new Date(durationResult.date).toISOString().split('T')[0]);
            }
          } catch (durationError) {
            console.error('Error fetching duration limit:', durationError);
          }

          // Ensure selectedItems has all required properties
          const safeSelectedItems = {
            files: Array.isArray(endowmentData.selectedItems?.files) ? endowmentData.selectedItems.files : [],
            passwords: Array.isArray(endowmentData.selectedItems?.passwords) ? endowmentData.selectedItems.passwords : [],
            banking: Array.isArray(endowmentData.selectedItems?.banking) ? endowmentData.selectedItems.banking : [],
            blockchain: Array.isArray(endowmentData.selectedItems?.blockchain) ? endowmentData.selectedItems.blockchain : []
          };

          const processedEndowment = {
            ...endowmentData,
            originalName: latestEndowment.name,
            status: status?.exists ? status.status : null,
            selectedItems: safeSelectedItems
          };

          setEndowment(processedEndowment);
          setEndowmentExists(true);

          // Set form values
          setAlias(endowmentData.alias || '');
          setRecipient(endowmentData.account || '');
          if (status?.exists && status.status?.release_date) {
            // Use the database release_date instead of file duration
            const dbReleaseDate = new Date(status.status.release_date).toISOString().split('T')[0];
            setReleaseDate(dbReleaseDate);
          } else {
            // Fallback to file duration if database date not available
            setReleaseDate(endowmentData.duration || '');
          }
          setRecipientStatus(status?.exists ? status.status : null);

          // Store original values for cancel functionality
          setOriginalValues({
            alias: endowmentData.alias || '',
            recipient: endowmentData.account || '',
            releaseDate: endowmentData.duration || ''
          });

          // Initialize selected items
          setSelectedItems(safeSelectedItems);

          // Show status alerts
          if (status?.exists && status.status?.is_declined) {
            showAlert(
              'This endowment has been rejected by the recipient. You can update the recipient email or resend the invitation.',
              'warning',
              10000
            );
          } else if (status?.exists && !status.status?.is_accepted) {
            showAlert(
              'This endowment is pending recipient approval. They received an email with instructions to accept or decline.',
              'info',
              10000
            );
          }

        } catch (error) {
          console.error('❌ Error loading endowment details:', error);
          showAlert(`Error loading endowment details: ${error.message}. Showing empty state.`, 'danger');
          resetForm();
        }
      } else {
        setEndowment(null);
        setEndowmentExists(false);
        resetForm();
      }
    } catch (error) {
      console.error('❌ Error loading endowment:', error);
      showAlert(`Error loading endowment: ${error.message}`, 'danger');
      resetForm();
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form to default values
  const resetForm = () => {
    setAlias('');
    setRecipient('');
    setReleaseDate('');
    setMaxReleaseDate('');
    setTermsAccepted(false);
    setIsEditing(false);
    setRecipientStatus(null);
    setOriginalValues({
      alias: '',
      recipient: '',
      releaseDate: ''
    });
    setSelectedItems({
      files: [],
      passwords: [],
      banking: [],
      blockchain: []
    });
  };

  // Handle create endowment
  const handleCreateEndowment = async () => {
    setIsSaving(true);
    try {
      // Check payment status first
      const paymentStatus = await checkPaymentStatus(token);
      if (paymentStatus.isOverdue) {
        throw new Error('Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.');
      }

      // Create empty selected items if creating new endowment
      const initialSelectedItems = {
        files: [],
        passwords: [],
        banking: [],
        blockchain: []
      };

      await endowmentService.uploadInheritance({
        alias: alias,
        account: recipient,
        selectedItems: initialSelectedItems,
        duration: releaseDate,
        Email: user.Email
      }, token);

      await loadEndowment(true);
      showAlert('Endowment created successfully', 'success');
      setIsEditing(false);
      setEndowmentExists(true);
    } catch (error) {
      showAlert(`Error creating endowment: ${error.message}`, 'danger');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle edit endowment
  const handleEditEndowment = async () => {
    setIsSaving(true);
    try {
      // **IMPROVED**: Get the current inheritance for accurate old values
      const currentInheritance = await endowmentService.getCurrentInheritance(token, user.Email);

      if (!currentInheritance) {
        throw new Error('No active endowment found to edit');
      }

      // Use editInheritance for changing alias or recipient email
      await endowmentService.editInheritance({
        oldAlias: currentInheritance.alias,     // Use current inheritance data
        newAlias: alias,                        // From form
        oldRecipientEmail: currentInheritance.account, // Use current inheritance data
        newRecipientEmail: recipient,           // From form
        selectedItems: currentInheritance.selectedItems, // Keep current items
        duration: releaseDate,                  // From form
        Email: user.Email
      }, token);

      // Clear cache and reload
      endowmentService.clearCache();
      await loadEndowment(true);
      showAlert('Endowment updated successfully', 'success');
      setIsEditing(false);
    } catch (error) {
      console.error('❌ Error updating endowment:', error);
      showAlert(`Error updating endowment: ${error.message}`, 'danger');
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete endowment
  const handleDeleteEndowment = async () => {
    setIsDeleting(true);
    try {
      if (endowment) {
        await endowmentService.deleteInheritance(endowment.originalName, token);
        showAlert('Endowment deleted successfully', 'success');
        resetForm();
        setEndowment(null);
        setEndowmentExists(false);
      }
    } catch (error) {
      showAlert(`Error deleting endowment: ${error.message}`, 'danger');
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle resend endowment
  const handleResendEndowment = async () => {
    setIsResending(true);
    try {
      if (!endowment || !recipientStatus) {
        throw new Error('Cannot resend this endowment - missing endowment or status data');
      }

      // Check if the endowment is actually declined
      if (!recipientStatus.is_declined) {
        throw new Error('This endowment has not been declined and cannot be resent');
      }

      // Get the inheritance ID from the status response
      // The ID might be in different places depending on your API response structure
      let inheritanceId = null;

      // Try to get the ID from various possible locations in the status object
      if (recipientStatus.inheritanceId) {
        inheritanceId = recipientStatus.inheritanceId;
      } else if (recipientStatus.id) {
        inheritanceId = recipientStatus.id;
      } else {
        // If we can't find the ID in status, we need to get it from the status call
        try {
          const statusResult = await endowmentService.getInheritanceStatus({
            creatorEmail: user.Email,
            recipientEmail: endowment.account
          }, token);

          if (statusResult.exists && statusResult.inheritanceId) {
            inheritanceId = statusResult.inheritanceId;
          } else if (statusResult.exists && statusResult.status && statusResult.status.inheritanceId) {
            inheritanceId = statusResult.status.inheritanceId;
          }
        } catch (statusError) {
          console.error('Error getting inheritance status for resend:', statusError);
        }
      }

      if (!inheritanceId) {
        throw new Error('Could not find inheritance ID for resend operation');
      }

      await endowmentService.resendInheritance({
        inheritanceId: inheritanceId,
        creatorEmail: user.Email
      }, token);

      showAlert('Endowment resent successfully. The recipient will receive a new invitation email.', 'success');

      // Refresh the endowment data to get updated status
      await loadEndowment(true);
    } catch (error) {
      console.error('Error resending endowment:', error);
      showAlert(`Error resending endowment: ${error.message}`, 'danger');
      throw error;
    } finally {
      setIsResending(false);
    }
  };

  // Handle recipient email change
  const handleRecipientChange = async (email) => {
    setRecipient(email);

    if (!isEditing || !email || email === originalValues.recipient) return;

    try {
      // Check if email already exists
      if (email.includes('@') && email.includes('.')) {
        const result = await endowmentService.checkRecipientEmail({
          email,
          creatorEmail: user.Email
        }, token);

        if (result.exists) {
          showAlert(result.message, 'warning');
                    // Log the check outcome (existing/conflict)
          logEvent('endowment_recipient_change', {
            recipient_domain: emailDomain(email),
            was_changed: email !== originalValues.recipient,
            check_result: 'exists_conflict'
          });
          return;
        }

        // If valid email, get duration
        const durationResult = await endowmentService.getDuration({
          creatorEmail: user.Email,
          recipientEmail: email
        }, token);

        if (durationResult.date) {
          setReleaseDate(new Date(durationResult.date).toISOString().split('T')[0]);
          setMaxReleaseDate(new Date(durationResult.date).toISOString().split('T')[0]);

          showAlert(
            `The release date has been set to ${new Date(durationResult.date).toLocaleDateString()}, which is the maximum allowed based on your subscription.`,
            'info',
            5000
          );

                    // Recipient successfully updated (no conflict), duration set
          logEvent('endowment_recipient_change', {
            recipient_domain: emailDomain(email),
            was_changed: email !== originalValues.recipient,
            check_result: 'ok_duration_set'
          });
        }
      }
    } catch (error) {
      console.error('Error validating recipient:', error);

           logEvent('endowment_recipient_change', {
        recipient_domain: emailDomain(email),
        was_changed: email !== originalValues.recipient,
        check_result: 'error',
        error_message: error?.message || 'unknown'
      });
    }
  };

  // Handle add items to endowment
  const handleAddItems = async (newSelectedItems) => {
    setIsAddingToEndowment(true);
    try {
      // Check payment status
      const paymentStatus = await checkPaymentStatus(token);
      if (paymentStatus.isOverdue) {
         logEvent('endowment_add_items', {
          files_added: 0, passwords_added: 0, banking_added: 0, blockchain_added: 0,
          duplicates_removed: 0, total_added: 0,
          add_result: 'blocked_overdue'
        });
        throw new Error('Your account is currently frozen. Please visit My Profile > Account to make payment and restore full access.');
      }

      // **IMPROVED**: Get the current inheritance to ensure we're working with the latest version
      const currentInheritance = await endowmentService.getCurrentInheritance(token, user.Email);

      if (!currentInheritance) {
        throw new Error('No active endowment found');
      }

      // Normalize for analytics
      const cur = currentInheritance.selectedItems || {};
      const curFiles = new Set(cur.files || []);
      const curPwds  = new Set(cur.passwords || []);
      const curBank  = new Set(cur.banking || []);
      const curChain = new Set(cur.blockchain || []);

      const reqFiles = Array.from(new Set(newSelectedItems.files || []));
      const reqPwds  = Array.from(new Set(newSelectedItems.passwords || []));
      const reqBank  = Array.from(new Set(newSelectedItems.banking || []));
      const reqChain = Array.from(new Set(newSelectedItems.blockchain || []));

      const actuallyNewFiles = reqFiles.filter(x => !curFiles.has(x));
      const actuallyNewPwds  = reqPwds.filter(x => !curPwds.has(x));
      const actuallyNewBank  = reqBank.filter(x => !curBank.has(x));
      const actuallyNewChain = reqChain.filter(x => !curChain.has(x));

      const files_added      = actuallyNewFiles.length;
      const passwords_added  = actuallyNewPwds.length;
      const banking_added    = actuallyNewBank.length;
      const blockchain_added = actuallyNewChain.length;
      const total_requested  = reqFiles.length + reqPwds.length + reqBank.length + reqChain.length;
      const total_added      = files_added + passwords_added + banking_added + blockchain_added;
      const duplicates_removed = total_requested - total_added;

      // Combine existing items with newly selected items
      const updatedItems = {
        files: [...new Set([...(currentInheritance.selectedItems.files || []), ...(newSelectedItems.files || [])])],
        passwords: [...new Set([...(currentInheritance.selectedItems.passwords || []), ...(newSelectedItems.passwords || [])])],
        banking: [...new Set([...(currentInheritance.selectedItems.banking || []), ...(newSelectedItems.banking || [])])],
        blockchain: [...new Set([...(currentInheritance.selectedItems.blockchain || []), ...(newSelectedItems.blockchain || [])])]
      };


      // Use uploadInheritance to update with new items
      await endowmentService.uploadInheritance({
        alias: currentInheritance.alias,
        account: currentInheritance.account,
        selectedItems: updatedItems,
        duration: currentInheritance.duration,
        Email: user.Email
      }, token);

      // Clear cache to prevent stale data issues
      endowmentService.clearCache();

      // Refresh endowment data
      await loadEndowment(true);
      showAlert('Items added to endowment successfully', 'success');

            logEvent('endowment_add_items', {
        files_added, passwords_added, banking_added, blockchain_added,
        duplicates_removed, total_added,
        add_result: 'success'
      });

    } catch (error) {
      console.error('❌ Error adding items to endowment:', error);
      showAlert(`Error adding items to endowment: ${error.message}`, 'danger');
      throw error;
    } finally {
      setIsAddingToEndowment(false);
    }
  };

  // Handle remove item from endowment
  const handleRemoveItem = async (itemName, itemType) => {
    setIsDeleting(true);
    try {
      // Get the current inheritance to ensure we're working with the latest version
      const currentInheritance = await endowmentService.getCurrentInheritance(token, user.Email);

      if (!currentInheritance) {
        throw new Error('No active endowment found');
      }


      const categoryMap = {
        'files': 'files',
        'passwords': 'passwords',
        'banking': 'banking',
        'blockchain': 'blockchain',
        // Also handle plural forms just in case
        'file': 'files',
        'password': 'passwords'
      };

      const category = categoryMap[itemType.toLowerCase()];

      if (!category) {
        throw new Error(`Unknown item type: ${itemType}`);
      }

      // Remove item from selected items
      const updatedItems = { ...currentInheritance.selectedItems };

      // Check if the category exists in selectedItems
      if (!Array.isArray(updatedItems[category])) {
        throw new Error(`Category ${category} not found in selectedItems`);
      }

      const index = updatedItems[category].indexOf(itemName);

      if (index === -1) {
        throw new Error('Item not found in the endowment');
      }

      updatedItems[category].splice(index, 1);


      // Use uploadInheritance to update with item removed
      await endowmentService.uploadInheritance({
        alias: currentInheritance.alias,
        account: currentInheritance.account,
        selectedItems: updatedItems,
        duration: currentInheritance.duration,
        Email: user.Email
      }, token);

      // Clear cache to prevent stale data issues
      endowmentService.clearCache();

      // Refresh endowment data
      await loadEndowment(true);
      showAlert('Item removed successfully', 'success');
    } catch (error) {
      console.error('❌ Error removing item:', error);
      showAlert(`Error removing item: ${error.message}`, 'danger');
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  // Load endowment on component mount
  useEffect(() => {
    if (token) {
      loadEndowment();
    }
  }, [token]);

  return {
    // State
    endowment,
    endowmentExists,
    isLoading,
    alias,
    recipient,
    releaseDate,
    maxReleaseDate,
    termsAccepted,
    isEditing,
    recipientStatus,
    originalValues,
    selectedItems,
    isSaving,
    isDeleting,
    isAddingToEndowment,
    isResending,
    alert,

    // Setters
    setAlias,
    setRecipient,
    setReleaseDate,
    setTermsAccepted,
    setIsEditing,
    setSelectedItems,
    setAlert,

    // Actions
    loadEndowment,
    resetForm,
    handleCreateEndowment,
    handleEditEndowment,
    handleDeleteEndowment,
    handleResendEndowment,
    handleRecipientChange,
    handleAddItems,
    handleRemoveItem,
    showAlert
  };
};